import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    ChevronLeft, Save, User, Building2, MapPin,
    Camera, Trash2, X, FileText, UserCheck, Home
} from 'lucide-react';
import { ClientService, SedeService } from '../services/apiService';
import { useAuth } from '../contexts/AuthContext';
import { UserRole, Sede } from '../types';
import { useToast } from "../components/ToastContext";
import { ColoreCalendarioField } from '../components/clienti/ColoreCalendarioField';

const FilePreviewItem = ({ file, onRemove, onPreview }: { file: File, onRemove: () => void, onPreview: (f: File) => void }) => {
    const isImage = file.type.startsWith('image/');
    const [previewUrl, setPreviewUrl] = React.useState<string | null>(null);

    React.useEffect(() => {
        if (isImage) {
            const url = URL.createObjectURL(file);
            setPreviewUrl(url);
            return () => URL.revokeObjectURL(url);
        }
    }, [file, isImage]);

    return (
        <div className="flex items-center justify-between bg-white p-2 rounded-lg border border-gray-200 shadow-sm text-sm group">
            <div className="flex items-center gap-3 overflow-hidden flex-1 cursor-pointer" onClick={() => onPreview(file)}>
                {isImage && previewUrl ? (
                    <img src={previewUrl} alt={file.name} className="w-10 h-10 object-cover rounded border border-gray-200" />
                ) : (
                    <div className="w-10 h-10 bg-gray-50 flex items-center justify-center rounded border border-gray-200">
                        <FileText size={20} className="text-gray-400" />
                    </div>
                )}
                <span className="truncate font-medium text-gray-700 group-hover:text-blue-600 transition-colors">{file.name}</span>
            </div>
            <button type="button" onClick={(e) => { e.stopPropagation(); onRemove(); }} className="text-red-500 p-2 hover:bg-red-50 rounded-lg ml-2 transition-colors">
                <Trash2 size={16} />
            </button>
        </div>
    );
};

export const NuovoClienteView: React.FC = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const { addToast } = useToast();
    const { user, currentSedeId } = useAuth();

    const [isLoading, setIsLoading] = useState(false);
    const [sedi, setSedi] = useState<Sede[]>([]);
    const [clientFiles, setClientFiles] = useState<File[]>([]);
    const [bypassDoc, setBypassDoc] = useState(false);
    const [previewFile, setPreviewFile] = useState<File | null>(null);

    const showSedePicker = user?.role === UserRole.ADMIN && currentSedeId === 'ALL';

    const [formData, setFormData] = useState({
        tipo: 'Privato',
        nome: '',
        surname: '',
        // Per i clienti Azienda i dati societari (ragione sociale, P.IVA, SDI, PEC,
        // sede legale) stanno nei campi nativi; nome/cognome sono del referente firmatario.
        ragioneSociale: '',
        pec: '',
        email: '',
        telefono: '',
        codiceFiscale: '',
        partitaIva: '',
        codiceSdi: '',
        indirizzo: '',
        citta: '',
        cap: '',
        provincia: '',
        luogoNascita: '',
        dataNascita: '',
        sedeId: '' as string | number,
        // Documento
        docTipo: 'Carta Identità',
        docNumero: '',
        docRilascioDa: '',
        docDataRilascio: '',
        docDataScadenza: '',
        // Dati referente persona fisica (per cliente Azienda).
        indirizzoReferente: '',
        cittaReferente: '',
        capReferente: '',
        provinciaReferente: '',
        emailReferente: '',
        telefonoReferente: '',
        // Per conto di terzi
        perContoDiTerzi: false,
        denominazioneTerzi: '',
        sedeTerzi: '',
        capTerzi: '',
        comuneTerzi: '',
        provinciaTerzi: '',
        pivaOrCfTerzi: '',
        codiceSdiTerzi: '',
        emailTerzi: '',
    });

    const set = (field: string, value: any) =>
        setFormData(prev => ({ ...prev, [field]: value }));

    useEffect(() => {
        if (showSedePicker) loadSedi();
        if (id) loadCliente(id);
    }, [id, user, currentSedeId]);

    const loadSedi = async () => {
        try {
            const res = await SedeService.getAll();
            if (res.success) setSedi(res.data);
        } catch (error) {
            console.error(error);
        }
    };

    const loadCliente = async (clienteId: string) => {
        setIsLoading(true);
        try {
            const res = await ClientService.getById(clienteId);
            if (res.success && res.data) {
                const c = res.data;
                // Azienda salvata col vecchio formato (ragione sociale nei campi "per conto di
                // terzi"): la riportiamo nei campi nativi, così al salvataggio finisce al posto giusto.
                const isAzienda = c.tipo === 'Azienda';
                const legacyRagioneSociale = isAzienda && !c.ragioneSociale
                    ? (c.denominazioneTerzi || '') : (c.ragioneSociale || '');
                const legacyPiva = isAzienda && !c.partitaIva
                    ? (c.pivaOrCfTerzi || '') : (c.partitaIva || '');
                const legacySdi = isAzienda && !c.codiceSdi
                    ? (c.codiceSdiTerzi || '') : (c.codiceSdi || '');
                const legacyPec = isAzienda && !c.pec
                    ? (c.emailTerzi || '') : (c.pec || '');
                // Azienda col vecchio formato: se i campi *Referente sono vuoti usiamo i nativi,
                // che prima contenevano i recapiti del referente.
                const legacyEmailRef    = isAzienda ? (c.emailReferente    || c.email    || '') : (c.emailReferente    || '');
                const legacyTelRef      = isAzienda ? (c.telefonoReferente || c.telefono || '') : (c.telefonoReferente || '');
                const legacyIndRef      = isAzienda ? (c.indirizzoReferente|| c.indirizzo|| '') : (c.indirizzoReferente|| '');
                const legacyCittaRef    = isAzienda ? (c.cittaReferente    || c.citta    || '') : (c.cittaReferente    || '');
                const legacyCapRef      = isAzienda ? (c.capReferente      || c.cap      || '') : (c.capReferente      || '');
                const legacyProvRef     = isAzienda ? (c.provinciaReferente|| c.provincia|| '') : (c.provinciaReferente|| '');
                setFormData({
                    tipo: c.tipo || 'Privato',
                    nome: c.nome || '',
                    surname: c.surname || '',
                    ragioneSociale: legacyRagioneSociale,
                    pec: legacyPec,
                    email: c.email || '',
                    telefono: c.telefono || '',
                    codiceFiscale: c.codiceFiscale || '',
                    partitaIva: legacyPiva,
                    codiceSdi: legacySdi,
                    indirizzo: c.indirizzo || '',
                    citta: c.citta || '',
                    cap: c.cap || '',
                    provincia: c.provincia || '',
                    luogoNascita: c.luogoNascita || '',
                    dataNascita: c.dataNascita || '',
                    sedeId: c.sedeId || '',
                    docTipo: c.docTipo || 'Carta Identità',
                    docNumero: c.docNumero || '',
                    docRilascioDa: c.docRilascioDa || '',
                    docDataRilascio: c.docDataRilascio || '',
                    docDataScadenza: c.docDataScadenza || '',
                    indirizzoReferente: legacyIndRef,
                    cittaReferente: legacyCittaRef,
                    capReferente: legacyCapRef,
                    provinciaReferente: legacyProvRef,
                    emailReferente: legacyEmailRef,
                    telefonoReferente: legacyTelRef,
                    perContoDiTerzi: !!c.perContoDiTerzi,
                    denominazioneTerzi: c.denominazioneTerzi || '',
                    sedeTerzi: c.sedeTerzi || '',
                    capTerzi: c.capTerzi || '',
                    comuneTerzi: c.comuneTerzi || '',
                    provinciaTerzi: c.provinciaTerzi || '',
                    pivaOrCfTerzi: c.pivaOrCfTerzi || '',
                    codiceSdiTerzi: c.codiceSdiTerzi || '',
                    emailTerzi: c.emailTerzi || '',
                });
            }
        } catch (error) {
            addToast("Errore caricamento cliente", "error");
        } finally {
            setIsLoading(false);
        }
    };

    const handleTipoChange = (nuovoTipo: 'Privato' | 'Azienda') => {
        if (nuovoTipo === formData.tipo) return;
        if (nuovoTipo === 'Privato') {
            setFormData(prev => ({
                ...prev,
                tipo: 'Privato',
                ragioneSociale: '', partitaIva: '', codiceSdi: '', pec: '',
                perContoDiTerzi: false,
                denominazioneTerzi: '', sedeTerzi: '', capTerzi: '', comuneTerzi: '',
                provinciaTerzi: '', pivaOrCfTerzi: '', codiceSdiTerzi: '', emailTerzi: '',
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                tipo: 'Azienda',
                perContoDiTerzi: false,
                denominazioneTerzi: '', sedeTerzi: '', capTerzi: '', comuneTerzi: '',
                provinciaTerzi: '', pivaOrCfTerzi: '', codiceSdiTerzi: '', emailTerzi: '',
            }));
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        const effectiveSedeId = formData.sedeId || (currentSedeId !== 'ALL' ? currentSedeId : '');
        if (user?.role === UserRole.ADMIN && !effectiveSedeId) {
            addToast("Devi selezionare una Sede per il cliente", "error");
            return;
        }
        if (!id && !bypassDoc && clientFiles.length === 0) {
            addToast("È obbligatorio acquisire il documento d'identità, oppure spunta la casella per proseguire senza.", "error");
            return;
        }

        setIsLoading(true);
        try {
            const clientData = { ...formData, sedeId: effectiveSedeId || formData.sedeId };
            if (user?.role !== UserRole.ADMIN) delete (clientData as any).sedeId;

            let res;
            if (id) {
                res = await ClientService.update(Number(id), clientData);
            } else {
                const payload = new FormData();
                payload.append("data", JSON.stringify(clientData));
                clientFiles.forEach(file => {
                    payload.append("files", new File([file], `DocID_${file.name}`, { type: file.type }));
                });
                res = await ClientService.createMultipart(payload);
            }

            if (res && res.success !== false) {
                addToast("Cliente salvato con successo", "success");
                navigate('/clienti');
            } else {
                addToast(res.message || "Errore salvataggio", "error");
            }
        } catch (error) {
            addToast("Errore di connessione", "error");
        } finally {
            setIsLoading(false);
        }
    };

    const inputCls = "w-full p-2.5 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none";

    return (
        <div className="px-4 lg:px-6 py-6 w-full max-w-4xl mx-auto pb-24">
            <div className="flex items-center gap-2 text-gray-500 hover:text-gray-900 cursor-pointer w-fit mb-6" onClick={() => navigate('/clienti')}>
                <ChevronLeft size={20} /> Torna alla lista
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-6">
                {id ? "Modifica Cliente" : "Nuovo Cliente"}
            </h1>

            <form onSubmit={handleSubmit} className="space-y-6" autoComplete="off">

                {/* Sede: solo per admin con tutte le sedi selezionate */}
                {showSedePicker && (
                    <div className="bg-orange-50 p-6 rounded-xl border border-orange-200">
                        <div className="flex items-center gap-2 text-orange-800 font-bold mb-4">
                            <Building2 size={20} /> Assegnazione Sede
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Seleziona Sede *</label>
                            <select
                                className="w-full p-2.5 border border-orange-300 rounded-lg bg-white outline-none focus:ring-2 focus:ring-orange-500"
                                value={formData.sedeId}
                                onChange={e => set('sedeId', e.target.value)}
                                required
                            >
                                <option value="">-- Seleziona --</option>
                                {sedi.map(s => <option key={s.id} value={s.id}>{s.name} ({s.citta})</option>)}
                            </select>
                        </div>
                    </div>
                )}

                {/* Tipo cliente */}
                <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                    <label className="block text-sm font-medium text-gray-700 mb-3">Tipo Cliente</label>
                    <div className="flex gap-4">
                        <label className={`flex-1 p-4 border rounded-xl cursor-pointer transition-all flex items-center justify-center gap-2 ${formData.tipo === 'Privato' ? 'border-blue-500 bg-blue-50 text-blue-700 font-bold' : 'hover:bg-gray-50'}`}>
                            <input type="radio" className="hidden" name="tipo" checked={formData.tipo === 'Privato'} onChange={() => handleTipoChange('Privato')} />
                            <User size={18} /> Privato
                        </label>
                        <label className={`flex-1 p-4 border rounded-xl cursor-pointer transition-all flex items-center justify-center gap-2 ${formData.tipo === 'Azienda' ? 'border-purple-500 bg-purple-50 text-purple-700 font-bold' : 'hover:bg-gray-50'}`}>
                            <input type="radio" className="hidden" name="tipo" checked={formData.tipo === 'Azienda'} onChange={() => handleTipoChange('Azienda')} />
                            <Building2 size={18} /> Azienda
                        </label>
                    </div>
                </div>

                {/* Dati anagrafici */}
                <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 space-y-4">
                    <h3 className="font-bold text-gray-900 border-b pb-2">Dati Anagrafici</h3>

                    {formData.tipo === 'Privato' ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Nome *</label>
                                <input required type="text" autoComplete="new-password" className={inputCls} value={formData.nome} onChange={e => set('nome', e.target.value)} />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Cognome *</label>
                                <input required type="text" autoComplete="new-password" className={inputCls} value={formData.surname} onChange={e => set('surname', e.target.value)} />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Luogo di Nascita</label>
                                <input type="text" autoComplete="new-password" className={inputCls} placeholder="Es. Roma" value={formData.luogoNascita} onChange={e => set('luogoNascita', e.target.value)} />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Data di Nascita</label>
                                <input type="date" autoComplete="new-password" className={inputCls} value={formData.dataNascita} onChange={e => set('dataNascita', e.target.value)} />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Codice Fiscale</label>
                                <input type="text" autoComplete="new-password" className={`${inputCls} uppercase`} value={formData.codiceFiscale} onChange={e => set('codiceFiscale', e.target.value)} />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Telefono</label>
                                <input type="text" autoComplete="new-password" className={inputCls} value={formData.telefono} onChange={e => set('telefono', e.target.value)} />
                            </div>
                            <div className="md:col-span-2">
                                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                                <input type="email" autoComplete="new-password" className={inputCls} value={formData.email} onChange={e => set('email', e.target.value)} />
                            </div>
                            <div className="md:col-span-2">
                                <label className="block text-sm font-medium text-gray-700 mb-1">Indirizzo di Residenza</label>
                                <div className="relative">
                                    <MapPin className="absolute left-2.5 top-2.5 text-gray-400" size={16} />
                                    <input type="text" autoComplete="new-password" className={`${inputCls} pl-8`} value={formData.indirizzo} onChange={e => set('indirizzo', e.target.value)} />
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Città</label>
                                <input type="text" autoComplete="new-password" className={inputCls} value={formData.citta} onChange={e => set('citta', e.target.value)} />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">CAP</label>
                                    <input type="text" autoComplete="new-password" className={inputCls} value={formData.cap} onChange={e => set('cap', e.target.value)} />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Prov.</label>
                                    <input type="text" autoComplete="new-password" className={`${inputCls} uppercase`} maxLength={2} value={formData.provincia} onChange={e => set('provincia', e.target.value)} />
                                </div>
                            </div>
                        </div>
                    ) : (
                        // Per l'Azienda qui ci sono i dati del referente persona fisica, con lo stesso
                        // layout del Privato. I campi *Referente evitano di sovrascrivere i recapiti
                        // dell'azienda, che stanno nei campi nativi (sub-form "Agisce per conto di").
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Nome *</label>
                                <input required type="text" autoComplete="new-password" className={inputCls} value={formData.nome} onChange={e => set('nome', e.target.value)} />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Cognome *</label>
                                <input required type="text" autoComplete="new-password" className={inputCls} value={formData.surname} onChange={e => set('surname', e.target.value)} />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Luogo di Nascita</label>
                                <input type="text" autoComplete="new-password" className={inputCls} placeholder="Es. Roma" value={formData.luogoNascita} onChange={e => set('luogoNascita', e.target.value)} />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Data di Nascita</label>
                                <input type="date" autoComplete="new-password" className={inputCls} value={formData.dataNascita} onChange={e => set('dataNascita', e.target.value)} />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Codice Fiscale</label>
                                <input type="text" autoComplete="new-password" className={`${inputCls} uppercase`} value={formData.codiceFiscale} onChange={e => set('codiceFiscale', e.target.value)} />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Telefono</label>
                                <input type="text" autoComplete="new-password" className={inputCls} value={formData.telefonoReferente} onChange={e => set('telefonoReferente', e.target.value)} />
                            </div>
                            <div className="md:col-span-2">
                                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                                <input type="email" autoComplete="new-password" className={inputCls} value={formData.emailReferente} onChange={e => set('emailReferente', e.target.value)} />
                            </div>
                            <div className="md:col-span-2">
                                <label className="block text-sm font-medium text-gray-700 mb-1">Indirizzo di Residenza</label>
                                <div className="relative">
                                    <MapPin className="absolute left-2.5 top-2.5 text-gray-400" size={16} />
                                    <input type="text" autoComplete="new-password" className={`${inputCls} pl-8`} value={formData.indirizzoReferente} onChange={e => set('indirizzoReferente', e.target.value)} />
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Città</label>
                                <input type="text" autoComplete="new-password" className={inputCls} value={formData.cittaReferente} onChange={e => set('cittaReferente', e.target.value)} />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">CAP</label>
                                    <input type="text" autoComplete="new-password" className={inputCls} value={formData.capReferente} onChange={e => set('capReferente', e.target.value)} />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Prov.</label>
                                    <input type="text" autoComplete="new-password" className={`${inputCls} uppercase`} maxLength={2} value={formData.provinciaReferente} onChange={e => set('provinciaReferente', e.target.value)} />
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/*
                  Agisce per conto di, unico blocco per Privato e Azienda.
                  - Privato: Proprio / Di terzi; il sotto-form usa i campi *Terzi.
                  - Azienda: sempre "Di terzi"; il sotto-form usa i campi nativi del Client
                  (ragioneSociale, sede legale, partitaIva, codiceSdi, pec).
                */}
                {(() => {
                    const isAzienda = formData.tipo === 'Azienda';
                    const diTerziChecked = isAzienda || formData.perContoDiTerzi;
                    const showSubForm = isAzienda || formData.perContoDiTerzi;
                    return (
                        <div className="bg-amber-50/50 p-6 rounded-xl border border-amber-100 space-y-4">
                            <h3 className="font-bold text-amber-800 border-b border-amber-200 pb-2 flex items-center gap-2">
                                <UserCheck size={18} /> Agisce per conto di
                            </h3>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <label
                                    className={`flex items-center gap-2 p-3 border rounded-lg ${
                                        isAzienda
                                            ? 'border-gray-200 bg-gray-50 opacity-50 cursor-not-allowed'
                                            : !formData.perContoDiTerzi
                                                ? 'border-blue-400 bg-blue-50 cursor-pointer'
                                                : 'border-gray-200 hover:bg-gray-50 cursor-pointer'
                                    }`}
                                    title={isAzienda ? "Per un cliente Azienda il referente agisce sempre per conto dell'azienda" : undefined}
                                >
                                    <input
                                        type="radio"
                                        className="text-blue-600"
                                        checked={!diTerziChecked}
                                        disabled={isAzienda}
                                        onChange={() => set('perContoDiTerzi', false)}
                                    />
                                    <span className="text-sm font-bold">Proprio</span>
                                </label>
                                <label className={`flex items-center gap-2 p-3 border rounded-lg cursor-pointer ${diTerziChecked ? 'border-amber-400 bg-amber-50' : 'border-gray-200 hover:bg-gray-50'}`}>
                                    <input
                                        type="radio"
                                        className="text-amber-600"
                                        checked={diTerziChecked}
                                        onChange={() => !isAzienda && set('perContoDiTerzi', true)}
                                    />
                                    <span className="text-sm font-bold">Di terzi</span>
                                </label>
                            </div>

                            {showSubForm && (
                                isAzienda ? (
                                    <div className="space-y-3 animate-in fade-in">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Denominazione (Ragione Sociale) *</label>
                                            <input
                                                required
                                                type="text"
                                                autoComplete="new-password"
                                                className={inputCls}
                                                value={formData.ragioneSociale}
                                                onChange={e => set('ragioneSociale', e.target.value)}
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Via Sede Legale</label>
                                            <div className="relative">
                                                <MapPin className="absolute left-2.5 top-2.5 text-gray-400" size={16} />
                                                <input type="text" autoComplete="new-password" className={`${inputCls} pl-8`} value={formData.indirizzo} onChange={e => set('indirizzo', e.target.value)} />
                                            </div>
                                        </div>
                                        <div className="grid grid-cols-3 gap-3">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">CAP</label>
                                                <input type="text" autoComplete="new-password" className={inputCls} value={formData.cap} onChange={e => set('cap', e.target.value)} />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">Comune</label>
                                                <input type="text" autoComplete="new-password" className={inputCls} value={formData.citta} onChange={e => set('citta', e.target.value)} />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">Prov.</label>
                                                <input type="text" autoComplete="new-password" maxLength={2} className={`${inputCls} uppercase`} value={formData.provincia} onChange={e => set('provincia', e.target.value)} />
                                            </div>
                                        </div>
                                        <div className="grid grid-cols-2 gap-3">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">Partita IVA</label>
                                                <input type="text" autoComplete="new-password" className={`${inputCls} font-mono uppercase`} value={formData.partitaIva} onChange={e => set('partitaIva', e.target.value)} />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">Codice SDI</label>
                                                <input type="text" autoComplete="new-password" maxLength={7} className={`${inputCls} uppercase font-mono`} value={formData.codiceSdi} onChange={e => set('codiceSdi', e.target.value)} />
                                            </div>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">PEC</label>
                                            <input type="email" autoComplete="new-password" className={inputCls} value={formData.pec} onChange={e => set('pec', e.target.value)} />
                                        </div>
                                    </div>
                                ) : (
                                    <div className="space-y-3 animate-in fade-in">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Denominazione</label>
                                            <input
                                                type="text"
                                                autoComplete="new-password"
                                                className={inputCls}
                                                value={formData.denominazioneTerzi}
                                                onChange={e => set('denominazioneTerzi', e.target.value)}
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Sede / Residenza</label>
                                            <input type="text" autoComplete="new-password" className={inputCls} placeholder="Via, Città..." value={formData.sedeTerzi} onChange={e => set('sedeTerzi', e.target.value)} />
                                        </div>
                                        <div className="grid grid-cols-3 gap-3">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">CAP</label>
                                                <input type="text" autoComplete="new-password" className={inputCls} value={formData.capTerzi} onChange={e => set('capTerzi', e.target.value)} />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">Comune</label>
                                                <input type="text" autoComplete="new-password" className={inputCls} value={formData.comuneTerzi} onChange={e => set('comuneTerzi', e.target.value)} />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">Prov.</label>
                                                <input type="text" autoComplete="new-password" maxLength={2} className={`${inputCls} uppercase`} value={formData.provinciaTerzi} onChange={e => set('provinciaTerzi', e.target.value)} />
                                            </div>
                                        </div>
                                        <div className="grid grid-cols-2 gap-3">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">P.IVA / C.F.</label>
                                                <input type="text" autoComplete="new-password" className={`${inputCls} uppercase`} value={formData.pivaOrCfTerzi} onChange={e => set('pivaOrCfTerzi', e.target.value)} />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">Codice SDI</label>
                                                <input type="text" autoComplete="new-password" className={`${inputCls} uppercase`} value={formData.codiceSdiTerzi} onChange={e => set('codiceSdiTerzi', e.target.value)} />
                                            </div>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Mail / PEC Soggetto Terzo</label>
                                            <input type="email" autoComplete="new-password" className={inputCls} value={formData.emailTerzi} onChange={e => set('emailTerzi', e.target.value)} />
                                        </div>
                                    </div>
                                )
                            )}
                        </div>
                    );
                })()}

                <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                    <ColoreCalendarioField
                        value={(formData as any).coloreCalendario}
                        onChange={v => setFormData({ ...formData, coloreCalendario: v } as any)}
                        clienteId={id ? Number(id) : undefined} />
                </div>

                {/* Documento d'identità */}
                <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 space-y-4">
                    <h3 className="font-bold text-gray-900 border-b pb-2">Documento d'Identità</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Tipo Documento</label>
                            <select className={`${inputCls} bg-white`} value={formData.docTipo} onChange={e => set('docTipo', e.target.value)}>
                                <option value="Carta Identità">Carta d'Identità</option>
                                <option value="Patente">Patente di Guida</option>
                                <option value="Passaporto">Passaporto</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Numero Documento</label>
                            <input type="text" autoComplete="new-password" className={`${inputCls} uppercase`} value={formData.docNumero} onChange={e => set('docNumero', e.target.value)} />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Rilasciato Da</label>
                            <input type="text" autoComplete="new-password" className={inputCls} placeholder="Es. Comune di Roma" value={formData.docRilascioDa} onChange={e => set('docRilascioDa', e.target.value)} />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Data Rilascio</label>
                            <input type="date" autoComplete="new-password" className={inputCls} value={formData.docDataRilascio} onChange={e => set('docDataRilascio', e.target.value)} />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Data Scadenza (Opzionale)</label>
                            <input type="date" autoComplete="new-password" className={inputCls} value={formData.docDataScadenza} onChange={e => set('docDataScadenza', e.target.value)} />
                        </div>
                    </div>

                    {/* Upload documento */}
                    <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 mt-2">
                        <label className="block text-sm font-bold text-slate-800 mb-3">
                            Acquisizione Immagini (Fronte/Retro e C.F.)
                        </label>
                        <label className={`flex flex-col items-center justify-center gap-2 bg-white border-2 border-dashed ${bypassDoc ? 'border-gray-200 opacity-50 cursor-not-allowed' : 'border-blue-300 hover:bg-blue-50 hover:border-blue-500 cursor-pointer'} p-6 rounded-xl transition-all`}>
                            <input
                                type="file"
                                className="hidden"
                                accept="image/*,application/pdf"
                                capture="environment"
                                multiple
                                disabled={bypassDoc}
                                onChange={(e) => {
                                    if (e.target.files) setClientFiles(prev => [...prev, ...Array.from(e.target.files!)]);
                                }}
                            />
                            <Camera size={28} className={bypassDoc ? "text-gray-300" : "text-blue-500"} />
                            <span className="text-sm font-bold text-gray-700 text-center">Scatta foto dal Tablet o Carica file</span>
                            <span className="text-xs text-gray-400 text-center">(Tocca qui per aprire la fotocamera)</span>
                        </label>

                        {clientFiles.length > 0 && !bypassDoc && (
                            <div className="mt-4 space-y-2">
                                {clientFiles.map((f, i) => (
                                    <FilePreviewItem
                                        key={i}
                                        file={f}
                                        onRemove={() => setClientFiles(prev => prev.filter((_, idx) => idx !== i))}
                                        onPreview={setPreviewFile}
                                    />
                                ))}
                            </div>
                        )}

                        <div className="mt-4 pt-4 border-t border-slate-200">
                            <label className="flex items-start gap-3 cursor-pointer group">
                                <input
                                    type="checkbox"
                                    className="mt-1 w-4 h-4 rounded text-amber-500 focus:ring-amber-500 border-gray-300 cursor-pointer"
                                    checked={bypassDoc}
                                    onChange={e => { setBypassDoc(e.target.checked); if (e.target.checked) setClientFiles([]); }}
                                />
                                <div className="flex flex-col">
                                    <span className="text-sm font-bold text-gray-800 group-hover:text-amber-600 transition-colors">Prosegui senza caricare i documenti d'identità</span>
                                    <span className="text-xs text-gray-500 mt-0.5">(Seleziona questa casella per ignorare il caricamento)</span>
                                </div>
                            </label>
                        </div>
                    </div>
                </div>

                {/*!id && (
                    <div className="p-3 bg-blue-50 text-blue-700 text-xs rounded-lg border border-blue-100 flex gap-2">
                        <Home size={14} className="shrink-0 mt-0.5" />
                        <p>Il cliente verrà salvato automaticamente alla creazione del documento.</p>
                    </div>
                )*/}

                <div className="flex justify-end pt-4">
                    <button type="submit" disabled={isLoading} className="bg-blue-600 text-white px-8 py-3 rounded-xl font-bold hover:bg-blue-700 shadow-lg shadow-blue-200 transition-all flex items-center gap-2 disabled:opacity-70">
                        {isLoading ? "Salvataggio..." : <><Save size={20} /> {id ? "Salva Modifiche" : "Crea Cliente"}</>}
                    </button>
                </div>
            </form>

            {previewFile && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4" onClick={() => setPreviewFile(null)}>
                    <div className="relative max-w-4xl w-full max-h-[90vh] flex flex-col items-center justify-center" onClick={e => e.stopPropagation()}>
                        <button onClick={() => setPreviewFile(null)} className="absolute -top-12 right-0 text-white hover:text-gray-300 bg-black/50 p-2 rounded-full transition-colors">
                            <X size={24} />
                        </button>
                        {previewFile.type.startsWith('image/') ? (
                            <img src={URL.createObjectURL(previewFile)} className="max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl" alt="Preview" />
                        ) : previewFile.type === 'application/pdf' ? (
                            <iframe src={URL.createObjectURL(previewFile)} className="w-full h-[85vh] rounded-lg shadow-2xl bg-white" title="PDF Preview" />
                        ) : (
                            <div className="bg-white p-8 rounded-xl flex flex-col items-center shadow-2xl">
                                <FileText size={64} className="text-gray-400 mb-4" />
                                <p className="text-lg font-bold text-gray-900 text-center">{previewFile.name}</p>
                                <p className="text-sm text-gray-500 mt-2">Anteprima non disponibile per questo formato.</p>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default NuovoClienteView;
